import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Flex, Text, Icon, Input, InputGroup, InputLeftElement, Select, Grid, VStack, Button, useBreakpointValue,
} from '@chakra-ui/react';
import { FiSearch, FiFolder, FiGrid, FiEye, FiPlus } from 'react-icons/fi';
import FichaPreviewPanel from './FichaPreviewPanel';

const CategoryChip = ({ label, icon, count, pastel, active, onClick }) => (
  <Flex as="button" onClick={onClick} align="center" gap="8px" px="14px" py="9px" borderRadius="13px"
    flexShrink={0} whiteSpace="nowrap"
    border="1.5px solid" borderColor={active ? 'coral.500' : 'cream.line'}
    bg={active ? 'coral.500' : 'white'} fontWeight="700" fontSize="13.5px"
    color={active ? 'white' : 'ink.700'} transition=".14s" _hover={{ borderColor: 'coral.500' }}>
    <Flex w="26px" h="26px" borderRadius="8px" align="center" justify="center" flexShrink={0}
      bg={active ? 'whiteAlpha.300' : `${pastel}.bg`} color={active ? 'white' : `${pastel}.ink`}>
      <Icon as={icon} boxSize="13px" />
    </Flex>
    {label}
    <Box as="span" px="7px" py="1px" borderRadius="999px" fontSize="11px" fontWeight="800"
      bg={active ? 'whiteAlpha.300' : 'cream.bg2'} color={active ? 'white' : 'ink.400'}>
      {count}
    </Box>
  </Flex>
);

const FichaCard = ({ item, selected, onClick }) => {
  const pastel = item.pastel || 'sky';
  return (
    <Flex as="button" onClick={onClick} align="stretch" w="100%" textAlign="left" mb="8px"
      bg="white" border="1px solid" borderColor={selected ? 'coral.500' : 'cream.line'}
      borderRadius="14px" overflow="hidden" boxShadow={selected ? '0 0 0 2px var(--chakra-colors-coral-soft)' : 'none'}
      transition=".15s">
      <Box w="5px" flexShrink={0} bg={`${pastel}.ink`} />
      <Flex flex={1} align="center" gap="14px" p="13px 16px" minW={0}>
        <Flex w="38px" h="38px" borderRadius="10px" bg={`${pastel}.bg`} color={`${pastel}.ink`}
          align="center" justify="center" flexShrink={0}>
          <Icon as={item.icon || FiFolder} boxSize="17px" />
        </Flex>
        <Box flex={1} minW={0}>
          <Text fontWeight="800" fontSize="13.5px" color="ink.900" noOfLines={1} letterSpacing="-0.01em">
            {item.survey?.name || 'Sin título'}
          </Text>
          <Flex align="center" gap="8px" mt="4px" flexWrap="wrap">
            <Text fontSize="11.5px" color="ink.400" fontWeight="500">
              {new Date(item.created_at).toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })}
            </Text>
            <Box as="span" px="7px" py="1px" borderRadius="6px" bg="cream.bg2" color="ink.400" fontSize="10.5px" fontWeight="700">
              #{item.id}
            </Box>
            <Box as="span" px="7px" py="1px" borderRadius="999px" bg={`${pastel}.bg`} color={`${pastel}.ink`} fontSize="10.5px" fontWeight="700">
              {item.categoryDisplayName}
            </Box>
          </Flex>
        </Box>
        <Icon as={FiEye} boxSize="14px" color={selected ? 'coral.500' : 'ink.400'} flexShrink={0} />
      </Flex>
    </Flex>
  );
};

const FichasListView = ({ categories = [], surveys = [], customer, onNewFicha, onOpenFicha }) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [previewItem, setPreviewItem] = useState(null);
  // En horizontal/desktop (lg+) la ficha se ve al costado; en mobile/tablet vertical se abre como modal
  const isSidePreview = useBreakpointValue({ base: false, lg: true });

  const handleSelectFicha = (item) => {
    if (isSidePreview) {
      setPreviewItem(item);
    } else {
      onOpenFicha(item);
    }
  };

  const categoryMeta = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.slug] = c; });
    return map;
  }, [categories]);

  const enriched = useMemo(() => surveys.map(item => {
    const slug = item.survey?.category || 'otros';
    const meta = categoryMeta[slug];
    return { ...item, pastel: meta?.pastel || 'sky', icon: meta?.icon || FiFolder, categoryDisplayName: meta?.displayName || slug };
  }), [surveys, categoryMeta]);

  const filtered = useMemo(() => {
    let list = enriched;
    if (activeCategory !== 'all') {
      list = list.filter(item => (item.survey?.category || 'otros') === activeCategory);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(item =>
        item.survey?.name?.toLowerCase().includes(term) ||
        item.categoryDisplayName?.toLowerCase().includes(term) ||
        item.id?.toString().includes(term)
      );
    }
    return [...list].sort((a, b) => {
      if (sortBy === 'name') return (a.survey?.name || '').localeCompare(b.survey?.name || '');
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [enriched, activeCategory, searchTerm, sortBy]);

  // Sin selección por defecto — solo limpia la selección si la ficha activa deja de estar visible
  useEffect(() => {
    if (previewItem && !filtered.some(item => item.id === previewItem.id)) {
      setPreviewItem(null);
    }
  }, [filtered, previewItem]);

  const grouped = useMemo(() => {
    const byYear = {};
    filtered.forEach(item => {
      const year = new Date(item.created_at).getFullYear();
      if (!byYear[year]) byYear[year] = [];
      byYear[year].push(item);
    });
    return Object.entries(byYear).sort((a, b) => b[0] - a[0]);
  }, [filtered]);

  // Orden plano tal como se ve en pantalla — usado para navegar con flechas
  const flatOrder = useMemo(() => grouped.flatMap(([, items]) => items), [grouped]);

  // Atajos de teclado: Escape cierra la previsualización, ↑/↓ navegan entre fichas.
  // Se ignoran mientras el foco está en un campo de búsqueda/select para no romper su uso normal.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (previewItem) setPreviewItem(null);
        return;
      }

      const tag = document.activeElement?.tagName;
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      if (isTyping || flatOrder.length === 0) return;

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const currentIndex = previewItem ? flatOrder.findIndex(i => i.id === previewItem.id) : -1;
        const lastIndex = flatOrder.length - 1;
        const nextIndex = e.key === 'ArrowDown'
          ? (currentIndex < 0 ? 0 : Math.min(currentIndex + 1, lastIndex))
          : (currentIndex < 0 ? lastIndex : Math.max(currentIndex - 1, 0));
        setPreviewItem(flatOrder[nextIndex]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [flatOrder, previewItem]);

  return (
    <Box fontFamily="jakarta">
      <Flex gap="12px" mb="18px" flexWrap="wrap" align="center">
        <InputGroup maxW="320px" flex={1}>
          <InputLeftElement pointerEvents="none">
            <Icon as={FiSearch} color="ink.400" boxSize="14px" />
          </InputLeftElement>
          <Input placeholder="Buscar por nombre, categoría o ID…" value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)} h="38px" borderRadius="10px" fontSize="13.5px"
            border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.900"
            _focus={{ borderColor: 'coral.500', boxShadow: 'none' }} />
        </InputGroup>
        <Select value={sortBy} onChange={e => setSortBy(e.target.value)} w="190px" h="38px" borderRadius="10px"
          fontSize="13px" fontWeight="600" border="1.5px solid" borderColor="cream.line2" bg="white" color="ink.700"
          _focus={{ borderColor: 'coral.500', boxShadow: 'none' }}>
          <option value="recent">Más reciente primero</option>
          <option value="oldest">Más antigua primero</option>
          <option value="name">Por nombre</option>
        </Select>
        <Button bg="coral.500" color="white" borderRadius="10px" fontWeight="700" fontSize="13.5px" h="38px"
          flexShrink={0} _hover={{ bg: 'coral.600' }} leftIcon={<Icon as={FiPlus} boxSize="14px" />}
          onClick={onNewFicha}>
          Nueva ficha
        </Button>
      </Flex>

      <Flex gap="9px" mb="22px" flexWrap="nowrap" overflowX="auto" className="custom-scrollbar" pb="2px">
        <CategoryChip label="Todas las categorías" icon={FiGrid} count={enriched.length} pastel="sky"
          active={activeCategory === 'all'} onClick={() => setActiveCategory('all')} />
        {categories.map(category => (
          <CategoryChip key={category.slug} label={category.displayName} icon={category.icon} count={category.count}
            pastel={category.pastel} active={activeCategory === category.slug}
            onClick={() => setActiveCategory(category.slug)} />
        ))}
      </Flex>

      {filtered.length === 0 ? (
        <Box bg="cream.bg2" borderRadius="16px" border="1.5px dashed" borderColor="cream.line2" p={8} textAlign="center">
          <Icon as={FiSearch} boxSize={8} color="ink.400" mb={3} />
          <Text fontSize="13.5px" color="ink.700" fontWeight="700">No se encontraron fichas</Text>
          <Text fontSize="12.5px" color="ink.400" mt={1}>Intenta modificar los filtros de búsqueda</Text>
        </Box>
      ) : (
        <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap="18px" alignItems="start">
          <Box>
            {grouped.map(([year, items]) => (
              <Box key={year} mb="22px">
                <Flex align="center" gap="12px" mb="12px">
                  <Text fontSize="12.5px" fontWeight="800" letterSpacing="0.06em" textTransform="uppercase" color="ink.400">
                    {year}
                  </Text>
                  <Box as="span" px="8px" py="1px" borderRadius="999px" bg="cream.bg2" color="ink.400" border="1px solid" borderColor="cream.line"
                    fontSize="11px" fontWeight="700">
                    {items.length} {items.length === 1 ? 'ficha' : 'fichas'}
                  </Box>
                  <Box flex={1} h="1px" bg="cream.line" />
                </Flex>
                <VStack spacing="0" align="stretch">
                  {items.map(item => (
                    <FichaCard key={item.id} item={item} selected={isSidePreview && previewItem?.id === item.id}
                      onClick={() => handleSelectFicha(item)} />
                  ))}
                </VStack>
              </Box>
            ))}
          </Box>

          <Box display={{ base: 'none', lg: 'block' }}>
            <FichaPreviewPanel item={previewItem} customer={customer} onOpenFicha={onOpenFicha} onClose={() => setPreviewItem(null)} />
          </Box>
        </Grid>
      )}
    </Box>
  );
};

export default FichasListView;
